import { useEffect, useState } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { Boxes, Package } from 'lucide-react';

import { queryKeys } from '@/lib/api/queryKeys';
import { brandInventoryApi, type BrandInventoryFilters } from './api';
import { useSelectedBrandId } from '@/lib/branding/brandContextStore';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
} from '@/components/ui/Card';
import { Skeleton } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/ui/EmptyState';
import { Pagination } from '@/components/ui/Pagination';
import { Badge } from '@/components/ui/Badge';
import { useT } from '@/lib/i18n/useT';
import { useFavoritable } from '@/features/favorites/FavoritableContext';
import { HelpHint } from '@/features/help/HelpHint';
import {
  BRAND_INVENTORY_STATES,
  type BrandInventoryState,
} from '@/lib/api/types';

/**
 * PRD §9 — Brand Inventory. Brand-level item pool (the "warehouse" upstream
 * of project Warehouse / xk_stock). Only surfaces when the brand has opted
 * in (Settings > Brand > Inventory). Filters by lifecycle state + tags.
 */
export function BrandInventoryListPage() {
  const t = useT();
  const selectedBrandId = useSelectedBrandId();
  useFavoritable(t('brandInventory.title'));

  const [filters, setFilters] = useState<BrandInventoryFilters>({
    page: 1,
    limit: 20,
    brandId: selectedBrandId ?? undefined,
  });

  // Re-scope the list whenever the user picks a different brand from the
  // workspace switcher. Resets to page 1 so we don't land on an out-of-range
  // page belonging to the previous brand.
  useEffect(() => {
    setFilters((f) => ({
      ...f,
      brandId: selectedBrandId ?? undefined,
      page: 1,
    }));
  }, [selectedBrandId]);

  const { data, isLoading, error } = useQuery({
    queryKey: queryKeys.brandInventory.list(
      filters.brandId,
      filters.state,
      filters.tag,
    ),
    queryFn: () => brandInventoryApi.list(filters),
    placeholderData: keepPreviousData,
  });

  function setStateFilter(state?: BrandInventoryState) {
    setFilters((f) => ({ ...f, state, page: 1 }));
  }

  function setPage(page: number) {
    setFilters((f) => ({ ...f, page }));
  }

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const page = filters.page ?? 1;
  const limit = filters.limit ?? 20;

  // Backend returns 400 when the brand has not opted in — surface as
  // empty state with the path to enable it, not as a fatal error.
  const isOptInError =
    error &&
    typeof (error as { message?: string }).message === 'string' &&
    /not enabled/i.test((error as { message: string }).message);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[var(--color-fg-primary)]">
            {t('brandInventory.title')}
            <HelpHint section="brand-inventory" />
          </h1>
          <p className="text-sm text-[var(--color-fg-muted)]">
            {t('brandInventory.subtitle')}
          </p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Boxes className="h-4 w-4" />
            {t('brandInventory.filterByState')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            <FilterChip
              label={t('brandInventory.allStates')}
              active={!filters.state}
              onClick={() => setStateFilter(undefined)}
            />
            {BRAND_INVENTORY_STATES.map((s) => (
              <FilterChip
                key={s}
                label={t(`brandInventory.state.${s}`)}
                active={filters.state === s}
                onClick={() => setStateFilter(s)}
              />
            ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="space-y-2 p-4">
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
            </div>
          ) : isOptInError ? (
            <EmptyState
              icon={<Package className="h-8 w-8" />}
              title={t('brandInventory.emptyNotEnabled')}
              description={t('brandInventory.emptyNotEnabledHint')}
            />
          ) : items.length === 0 ? (
            <EmptyState
              icon={<Package className="h-8 w-8" />}
              title={t('brandInventory.empty')}
              description={t('brandInventory.emptyHint')}
            />
          ) : (
            <table className="w-full text-sm">
              <thead className="border-b border-[var(--color-border-subtle)] text-left text-xs uppercase text-[var(--color-fg-muted)]">
                <tr>
                  <th className="px-4 py-3">{t('brandInventory.col.name')}</th>
                  <th className="px-4 py-3">{t('brandInventory.col.quantity')}</th>
                  <th className="px-4 py-3">{t('brandInventory.col.state')}</th>
                  <th className="px-4 py-3">{t('brandInventory.col.rating')}</th>
                  <th className="px-4 py-3">{t('brandInventory.col.tags')}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((row) => (
                  <tr
                    key={row.id}
                    className="border-b border-[var(--color-border-subtle)] hover:bg-[var(--color-xk-surface)]"
                  >
                    <td className="px-4 py-3 font-medium text-[var(--color-fg-primary)]">
                      <div className="flex items-center gap-3">
                        {row.image ? (
                          <img
                            src={row.image}
                            alt={row.name}
                            className="h-10 w-10 rounded object-cover"
                          />
                        ) : (
                          <div className="grid h-10 w-10 place-items-center rounded bg-[var(--color-xk-surface-2)] text-[var(--color-fg-muted)]">
                            <Package className="h-4 w-4" />
                          </div>
                        )}
                        <span>{row.name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 tabular-nums">
                      {row.quantity}
                      {row.estimated && (
                        <span className="ml-2 text-xs text-[var(--color-fg-muted)]">
                          ({t('brandInventory.estimated')})
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <Badge tone={badgeToneForState(row.state)}>
                        {t(`brandInventory.state.${row.state}`)}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 tabular-nums">
                      {row.rating ? `★ ${row.rating}` : '—'}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {(row.tags ?? []).map((tag) => (
                          <span
                            key={tag}
                            className="rounded bg-[var(--color-xk-surface-2)] px-2 py-0.5 text-xs text-[var(--color-fg-muted)]"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {total > limit && (
        <Pagination
          page={page}
          pageCount={Math.max(1, Math.ceil(total / limit))}
          total={total}
          from={total === 0 ? 0 : (page - 1) * limit + 1}
          to={Math.min(page * limit, total)}
          setPage={setPage}
          next={() =>
            setPage(Math.min(page + 1, Math.ceil(total / limit)))
          }
          prev={() => setPage(Math.max(page - 1, 1))}
          noun={t('brandInventory.noun')}
        />
      )}
    </div>
  );
}

function FilterChip({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        'rounded-full border px-3 py-1 text-xs font-medium transition-colors',
        active
          ? 'border-[var(--color-xk-accent)] bg-[var(--color-accent-15)] text-[var(--color-xk-accent)]'
          : 'border-[var(--color-border-subtle)] text-[var(--color-fg-muted)] hover:bg-[var(--color-xk-surface)]',
      ].join(' ')}
    >
      {label}
    </button>
  );
}

function badgeToneForState(
  state: BrandInventoryState,
): 'neutral' | 'success' | 'warn' | 'error' | 'info' | 'accent' {
  switch (state) {
    case 'unassigned':
      return 'neutral';
    case 'allocated':
      return 'info';
    case 'distributed':
      return 'accent';
    case 'claimed':
      return 'success';
    case 'returned':
      return 'warn';
    case 'written_off':
      return 'error';
  }
}
